import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions.d';
import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { bulkResponseHandler } from '@apis/bulkResponseHandler';
import { baseQueryWithIntercept } from '@apis/fetchBaseQuery';
import {
    REQUESTS_PATH,
    REQUESTS_PATH_DEFECTIVE
} from '@apis/outboundByBulk/OutboundByBulk.type';
import { QueryWithIntercept } from '@apis/queryWithIntercept';
import { RequestStatusWithEmptyData } from '@customTypes/api/api.types';
import {
    BULK_TEMPLATES_NAMES,
    ColumnMappingDTO,
    ColumnMappingResponse,
    DownloadTemplateDTO,
    GetAvailableServicesDTO,
    GetAvailableServicesResponse,
    GroupingOrdersDTO,
    GroupingOrdersResponse,
    SubmitOrdersDTO,
    ValidateFileDTO,
    ValidateFileResponse
} from '@customTypes/bulkOrder/bulk.types';
import { getServicesWithIds } from '@helpers/activeOrder';

const getCommonEndpoints = (
    builder: EndpointBuilder<QueryWithIntercept, never, string>,
    isDefective: boolean
) => {
    const requestsPath = isDefective ? REQUESTS_PATH_DEFECTIVE : REQUESTS_PATH;
    const fileName = isDefective
        ? BULK_TEMPLATES_NAMES.RETURNS_DEFECTIVE_FROM_CHOICE
        : BULK_TEMPLATES_NAMES.CUSTOMER_GOOD;

    return {
        getDownloadOutboundTemplate: builder.mutation<
            File,
            DownloadTemplateDTO
        >({
            query: (dto) => ({
                url: requestsPath.DOWNLOAD_TEMPLATE,
                headers: {
                    responseType: 'blob',
                    'content-type': 'application/octet-stream'
                },
                responseHandler: async (response) =>
                    bulkResponseHandler(response, dto, fileName),
                cache: 'no-cache',
                method: 'POST',
                body: dto
            })
        }),
        resetOutboundFile: builder.mutation<RequestStatusWithEmptyData, string>(
            {
                query: (id) => ({
                    url: requestsPath.RESET_FILE.replace(':id', id),
                    method: 'DELETE'
                }),
                extraOptions: {
                    flowEnd: true
                }
            }
        ),
        getOutboundColumnsMapping: builder.mutation<
            ColumnMappingResponse,
            ColumnMappingDTO
        >({
            query: (dto) => ({
                url: requestsPath.GET_COLUMNS_MAPPING,
                method: 'POST',
                body: dto,
                cache: 'no-cache'
            })
        }),
        validateOutboundFile: builder.mutation<
            ValidateFileResponse,
            ValidateFileDTO
        >({
            query: (dto) => ({
                url: requestsPath.VALIDATE_FILE,
                method: 'POST',
                body: dto,
                cache: 'no-cache'
            })
        }),
        getOutboundGroupingOrders: builder.mutation<
            GroupingOrdersResponse,
            GroupingOrdersDTO
        >({
            query: (dto) => ({
                url: requestsPath.GET_GROUPING,
                method: 'POST',
                body: dto,
                cache: 'no-cache'
            })
        }),
        getOutboundAvailableServices: builder.mutation<
            GetAvailableServicesResponse,
            GetAvailableServicesDTO
        >({
            query: (dto) => ({
                url: requestsPath.GET_AVAILABLE_SERVICES,
                method: 'POST',
                body: dto,
                cache: 'no-cache'
            }),
            transformResponse(response: GetAvailableServicesResponse) {
                return getServicesWithIds(
                    response
                ) as GetAvailableServicesResponse;
            }
        }),
        submitOutboundOrders: builder.mutation<
            RequestStatusWithEmptyData,
            SubmitOrdersDTO
        >({
            query: (dto) => ({
                url: requestsPath.SUBMIT_ORDERS,
                method: 'POST',
                body: dto,
                cache: 'no-cache'
            }),
            extraOptions: {
                flowEnd: true
            }
        })
    };
};

export const outboundByBulkApi = createApi({
    reducerPath: 'outboundByBulkApi',
    baseQuery: baseQueryWithIntercept,
    endpoints: (builder) => getCommonEndpoints(builder, false)
});
export const outboundByBulkDefectiveApi = createApi({
    reducerPath: 'outboundByBulkDefectiveApi',
    baseQuery: baseQueryWithIntercept,
    endpoints: (builder) => getCommonEndpoints(builder, true)
});

export const {
    useGetDownloadOutboundTemplateMutation,
    useResetOutboundFileMutation,
    useGetOutboundColumnsMappingMutation,
    useValidateOutboundFileMutation,
    useGetOutboundGroupingOrdersMutation,
    useSubmitOutboundOrdersMutation,
    useGetOutboundAvailableServicesMutation
} = outboundByBulkApi;

export const {
    useGetDownloadOutboundTemplateMutation:
        useDefectiveGetDownloadOutboundTemplateMutation,
    useResetOutboundFileMutation: useDefectiveResetOutboundFileMutation,
    useGetOutboundColumnsMappingMutation:
        useDefectiveGetOutboundColumnsMappingMutation,
    useValidateOutboundFileMutation: useDefectiveValidateOutboundFileMutation,
    useGetOutboundGroupingOrdersMutation:
        useGetDefectiveOutboundGroupingOrdersMutation,
    useSubmitOutboundOrdersMutation: useSubmitDefectiveOutboundOrdersMutation,
    useGetOutboundAvailableServicesMutation:
        useGetDefectiveOutboundAvailableServicesMutation
} = outboundByBulkDefectiveApi;
